import { findLeaf, mappedLeaf, leafPoint, leafOffset } from './leaf-dom'
import { FIELD_ROW } from '../core/scope'
import type { Block, Blocks } from '@w/content-blocks'
import { tableGrid } from '@w/content-blocks/src/table-html'
import type { CorePoint, CoreState } from '../core/state'
import { children, findBlock, gapOf, locate, plain, rows } from '../core/tree-read'
import { blockClass } from '../core/block-selection'
import { tableShape } from '../core/rich-content'
import type { HarnessAction } from '../conformance/harness-actions'

/** Elements the user edits as text: every `contenteditable` but "false". Editor roots are `plaintext-only` (React then
 * skips its page-sized selection save around each commit, core-editor.tsx); fields and other inputs are "true". */
export const EDITABLE = '[contenteditable]:not([contenteditable="false"])'

/** The first element under `root` rendering block `id`, found by the engine's attribute selector instead of a script
 * pass over every block element (domPoint ran one per selection restore: 1.4 ms per keystroke at 2,000 blocks). */
export function renderedBlock(root: ParentNode, id: string): HTMLElement | undefined {
  if (!globalThis.CSS?.escape) return Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]')).find(node => node.dataset.blockId === id)
  return root.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(id)}"]`) ?? undefined
}

export function domPoint(root: HTMLElement, point: CorePoint, blocks?: Blocks) {
  const leaf = findLeaf(root, point.blockId)
  if (leaf) return leafPoint(leaf, point.offset)
  const element = point.blockId === 'root' || point.blockId === FIELD_ROW ? root
    : renderedBlock(root, point.blockId)
  if (!element && blocks) {
    // A view may render a block's content as one opaque presentation (code, a formula, an answer-mode cloze's input):
    // a point inside it is the gap before (offset 0) or after it, beside its nearest rendered ancestor.
    const ancestors = (items: Blocks): Block[] | undefined => {
      for (const block of items) {
        if (block.id === point.blockId) return []
        const path = ancestors(children(block))
        if (path) return [block, ...path]
      }
      return undefined
    }
    const rendered = ancestors(blocks)?.toReversed().map(block => renderedBlock(root, block.id)).find(found => found !== undefined)
    if (rendered?.parentElement) return { node: rendered.parentElement, offset: [...rendered.parentElement.childNodes].indexOf(rendered) + (point.offset > 0 ? 1 : 0) }
  }
  if (!element) throw new Error(`Missing DOM block ${point.blockId}`)
  if ((element.dataset.inlineTextPayload || element.tagName === 'BR') && element.parentElement) {
    // An atom or `br` point is the gap before (0) or after (1) its element.
    return { node: element.parentElement,
      offset: [...element.parentElement.childNodes].indexOf(element) + point.offset }
  }
  if (element.dataset.leaf === 'true') {
    const walker = document.createTreeWalker(element,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT)
    let remaining = point.offset
    let last: { node: Element | Text; offset: number } = { node: element, offset: 0 }
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node instanceof Text) {
        if (remaining <= node.length) return { node, offset: remaining }
        remaining -= node.length
        last = { node, offset: node.length }
      } else if (node instanceof HTMLBRElement && node.parentElement) {
        const index = [...node.parentElement.childNodes].indexOf(node)
        if (remaining === 0) return { node: node.parentElement, offset: index }
        remaining -= 1
        last = { node: node.parentElement, offset: index + 1 }
      }
    }
    return last
  }
  const owned = domChildren(element, element === root && blocks?.[0]?.id !== FIELD_ROW ? blocks : undefined)
  const next = owned[point.offset]
  const previous = owned.at(-1)
  let child = next ?? previous
  if (element === root) {
    while (child?.parentElement && child.parentElement !== root) child = child.parentElement
  }
  if (child?.parentElement) {
    return { node: child.parentElement,
      offset: [...child.parentElement.childNodes].indexOf(child) + (next ? 0 : 1) }
  }
  return { node: element, offset: 0 }
}

export function corePoint(root: HTMLElement, node: Node | null, offset: number, blocks?: Blocks): CorePoint | null {
  if (!node || !root.contains(node)) return null
  const leaf = mappedLeaf(node)
  if (leaf) {
    const current = blocks && findBlock(blocks, leaf.id)
    const position = leafOffset(leaf, node, offset)
    if (blocks && (!current || position > plain(current).length)) return null
    return { blockId: leaf.id, offset: position }
  }
  if (node === root) {
    if (blocks?.[0]?.id === FIELD_ROW) return { blockId: FIELD_ROW, offset }
    if (root.matches('[data-core-field], [data-core-scope]')) return { blockId: 'root', offset }
    // Root DOM children include title/overlay chrome and page comment controls/threads.
    // Core gaps count only body blocks.
    const before = new Set([...root.childNodes].slice(0, offset))
    const position = domChildren(root, blocks).filter(child => {
      let top: Node = child
      while (top.parentNode && top.parentNode !== root) top = top.parentNode
      return before.has(top as ChildNode)
    }).length
    return { blockId: 'root', offset: position }
  }
  const element = node instanceof Element ? node : node.parentElement
  const block = element?.closest<HTMLElement>('[data-block-id]')
  if (!block?.dataset.blockId) return null
  // The DOM may still show blocks a remote import removed before React re-renders; such a point names no block of
  // the current state, so it is not a selection. The core selection importRemote already remapped stays.
  const current = blocks && findBlock(blocks, block.dataset.blockId)
  if (blocks && !current) return null
  if (block.tagName === 'HR') return { blockId: block.dataset.blockId, offset: 0 }
  if (block.dataset.leaf === 'true') {
    const range = document.createRange()
    range.setStart(block, 0)
    range.setEnd(node, offset)
    const leafOffset = range.toString().length + range.cloneContents().querySelectorAll('br').length
    // Likewise the DOM may still show a leaf's longer pre-import text (a peer deleted or merged it away); an offset past
    // the current leaf's end names no point of the current state, so it is not a selection either.
    if (current && leafOffset > plain(current).length) return null
    return { blockId: block.dataset.blockId, offset: leafOffset }
  }
  const boundary = document.createRange()
  boundary.setStart(node, offset)
  boundary.collapse(true)
  const owned = domChildren(block)
  const position = owned.filter((child) => {
    const before = document.createRange()
    before.setStartBefore(child)
    before.collapse(true)
    return boundary.compareBoundaryPoints(Range.START_TO_START, before) > 0
  }).length
  // A DOM gap beside an atom or grid inside a container is that block's edge, the gap's canonical name.
  const edge = (element: HTMLElement | Text | undefined, offset: 0 | 1) => {
    const id = element instanceof HTMLElement ? element.dataset.blockId : element && mappedLeaf(element)?.id
    const beside = id === undefined || !blocks ? undefined : findBlock(blocks, id)
    return beside && ['atom', 'grid'].includes(blockClass(beside)) ? { blockId: beside.id, offset } : null
  }
  const owner = blocks ? findBlock(blocks, block.dataset.blockId) : undefined
  // A column is a grid slot holding blocks, as a container does.
  if (owner && (['container', 'list'].includes(blockClass(owner)) || owner.type === 'col')) {
    const named = edge(owned[position], 0) ?? edge(owned[position - 1], 1)
    if (named) return named
  }
  return { blockId: block.dataset.blockId, offset: position }
}

export function domChildren(element: HTMLElement, blocks?: Blocks): (HTMLElement | Text)[] {
  const ids = blocks && new Set(blocks.map(block => block.id))
  const bare = Array.from(element.childNodes).filter((node): node is Text => node instanceof Text && mappedLeaf(node)?.nodes[0] === node)
  const elements = [...element.querySelectorAll<HTMLElement>('[data-block-id]')].filter((child) => {
    if (ids && !ids.has(child.dataset.blockId ?? '')) return false
    const owner = child.parentElement?.closest('[data-block-id]')
    return owner === element || !element.dataset.blockId && (!owner || !element.contains(owner))
  })
  if (bare.length === 0) return elements
  return [...bare, ...elements].toSorted((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)
}

interface PathNode { point: CorePoint; children: PathNode[]; text?: boolean }
/** Imported paths describe semantic shell nodes. Table cells add a paragraph;
 * code controls and core formatting spans are view chrome, not path components. */
function project(block: Block): PathNode {
  const point = { blockId: block.id, offset: 0 }
  if (block.type === 'chars' || block.type === 'text') {
    return { point, children: [{ point, children: [], text: true }] }
  }
  let nested = children(block).map((child) => project(child))
  if (block.type === 'table') nested = [{ point, children: nested }]
  if (block.type === 'td') nested = [{ point, children: nested }]
  if (block.type === 'pre') nested = [{ point, children: [] },
    { point, children: [{ point, children: nested }] }]
  return { point, children: nested }
}

/** An imported Lexical DOM-path selection assertion, observed from the native DOM selection. */
export function assertDOMSelection(root: HTMLElement, blocks: Blocks,
  action: Extract<HarnessAction, { type: 'assertDOMSelection' }>): void {
  const entries = rows(blocks)
  const tree: PathNode = { point: { blockId: 'root', offset: 0 },
    children: blocks.map((block) => project(block)) }
  const resolve = (path: number[], offset: number) => {
    let node = tree
    for (const index of path) {
      const child = node.children[index]
      if (!child) throw new Error(`No semantic selection node at ${path.join('.')}`)
      node = child
    }
    return { ...node.point, offset }
  }
  const native = root.ownerDocument.getSelection()
  if (!native?.rangeCount) throw new Error('INV-NATIVE: missing DOM selection')
  const anchor = corePoint(root, native.anchorNode, native.anchorOffset)
  const focus = native.anchorNode === native.focusNode && native.anchorOffset === native.focusOffset
    ? anchor && { ...anchor }
    : corePoint(root, native.focusNode, native.focusOffset)
  if (!anchor || !focus) throw new Error('INV-NATIVE: selection outside editor')
  const selection = { anchor, focus }
  const assertPoint = (actual: CorePoint, path: number[], offset: number | number[]) => {
    const lower = resolve(path, Array.isArray(offset) ? offset[0] : offset)
    const upper = resolve(path, Array.isArray(offset) ? offset[1] : offset)
    const gap = actual.blockId === 'root' ? { siblings: blocks, index: actual.offset } : gapOf(blocks, actual)
    if (gap?.siblings === blocks && lower.blockId === 'root') {
      expect(gap.index).toBeGreaterThanOrEqual(lower.offset)
      expect(gap.index).toBeLessThanOrEqual(upper.offset)
      return
    }
    const point = locate(entries, actual)
    const from = locate(entries, lower)
    const to = locate(entries, upper)
    expect(point.row).toBe(from.row)
    expect(point.offset).toBeGreaterThanOrEqual(from.offset)
    expect(point.offset).toBeLessThanOrEqual(to.offset)
  }
  assertPoint(selection.anchor, action.anchorPath, action.anchorOffset)
  assertPoint(selection.focus, action.focusPath, action.focusOffset)
}

/** Selection projection derives cell highlighting solely from CoreSelection. */
export function selectedCellIds(state: CoreState): Set<string> {
  const selected = new Set<string>()
  const selection = state.selection
  if (!selection) return selected
  const entries = rows(state.blocks)
  const range = selection.table
  if (range) {
    const table = entries.flatMap((entry) => entry.parents)
      .find((block) => block.id === range.tableId)
    if (table?.type !== 'table') return selected
    const grid = tableGrid(table)
    const { from, to } = tableShape(grid, range)
    for (let y = from.y; y <= to.y; y++) {
      for (let x = from.x; x <= to.x; x++) {
        const cell = grid[y]?.[x]
        if (cell) selected.add(cell.id)
      }
    }
    return selected
  }
  if (entries.length === 0) return selected
  const anchor = locate(entries, selection.anchor)
  const focus = locate(entries, selection.focus)
  const from = Math.min(anchor.row, focus.row)
  const to = Math.max(anchor.row, focus.row)
  for (const [index, entry] of entries.entries()) {
    const table = entry.parents.find((block) => block.type === 'table')
    if (!table) continue
    const inside = (point: CorePoint, row: number) => !gapOf(state.blocks, point) &&
      entries[row].parents.includes(table)
    if (selection.nodes?.includes(table.id) || index >= from && index <= to &&
      inside(selection.anchor, anchor.row) !== inside(selection.focus, focus.row)) {
      selected.add(entry.block.id)
    }
  }
  return selected
}
